import { FontIcon } from '@intigral/shared';
import { useContext, useEffect, useMemo } from 'react';
import useToggleDetailOverlay from '../../../hooks/useToggleDetailOverlay';
import { useSelector } from 'react-redux';
import { getCountryPPVEvents } from '../../../utils/user';
import { useCountryCode, useConfigurationSections } from '../../../hooks/useConfiguration';
import { usePPVEvents } from '../../../hooks/usePPVEvents';
import { useSharedPromotions } from '../../../hooks/usePromotions';
import { IStore } from '../../../interfaces/StoreTypes';
import { __ } from '../../../translations';
import h from '../../../helpers/common';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { toast } from '@intigral/shared';
import moment from 'moment';
import { EVENT } from '../../../contexts/MixpanelConstants';
import channelsUtil from '../../../helpers/channelsUtil';

export const useSelectedPPV = (
    selectedPPVId,
    setSelectedChannel,
    ppvSubscribedList,
    onPurchaseSuccess,
    selectedPPVEvent,
    setSelectedPPVEvent
) => {
    const { userChannels } = useSelector((state: IStore) => state.liveTv);

    const { data: ppvEventConfig } = usePPVEvents();
    const { data: promotions } = useSharedPromotions();
    const countryCode = useCountryCode();
    const { channelImage = {} as any, operators, ppvConfigV2 } = useConfigurationSections();
    const { id: channelImageId = 'colour_264x26' } = channelImage;
    const { events: cmsPPVEvents = null } = ppvConfigV2 || {};
    const { track } = useContext(MixpanelContext) || {};
    const { removeSearchParams } = useToggleDetailOverlay();
    const { isNow, isEarlier } = channelsUtil;

    const ppvEvents = useMemo(() => {
        return getCountryPPVEvents(ppvEventConfig, operators, countryCode);
    }, [ppvEventConfig, operators, countryCode]);

    useEffect(() => {
        if (selectedPPVId && userChannels?.channels.length) {
            // Try to find in filtered ppvEvents first
            let event = ppvEvents?.find((event) => event.eventId === selectedPPVId);

            // If not found in filtered list, try to find in raw ppvEventConfig (nested structure)
            if (!event && ppvEventConfig && ppvEventConfig.length > 0) {
                // ppvEventConfig has nested structure: countries[].paymentMethods[].events[]
                for (const country of ppvEventConfig) {
                    if (country.paymentMethods) {
                        for (const paymentMethod of country.paymentMethods) {
                            if (paymentMethod.events) {
                                const foundEvent = paymentMethod.events.find((e) => e.eventId === selectedPPVId);
                                if (foundEvent) {
                                    event = foundEvent;
                                    break;
                                }
                            }
                        }
                        if (event) break;
                    }
                }
            }

            if (event) {
                // Try to find event data from promotions (for banner image)
                // Try multiple matching strategies to find promotion with image
                const eventData =
                    promotions && (promotions as any).length
                        ? (promotions as any).find((item) => {
                              // Strategy 1: Match by subscription GUID (original)
                              const matchesBySubscription = item?.subscriptionGuids?.includes(event?.subscriptionGuid);

                              // Strategy 2: Match by program GUID
                              const matchesByProgramGuid =
                                  item?.guid === event?.programGuid || item?.programGuid === event?.programGuid;

                              // Strategy 3: Match by event ID
                              const matchesByEventId = item?.eventId === event?.eventId;

                              return matchesBySubscription || matchesByProgramGuid || matchesByEventId;
                          })
                        : null;

                const selectedChannel = userChannels?.channels.find((channel) =>
                    channel?.subscriptions?.find((item) => item?.subscriptionGUID === event?.subscriptionGuid)
                );

                const channelLogoSrc = channelImageId.toLowerCase().includes('svg')
                    ? selectedChannel?.channelImages?.find((img) => img.id === channelImageId)?.url ||
                      (selectedChannel?.channelImages[0] && selectedChannel?.channelImages[0].url) ||
                      ''
                    : h.getImagizerUrl(
                          selectedChannel?.channelImages?.find((img) => img.id === channelImageId)?.url ||
                              (selectedChannel?.channelImages[0] && selectedChannel?.channelImages[0].url) ||
                              '',
                          176
                      );
                setSelectedChannel({
                    ...selectedChannel,
                    channelLogoSrc,
                });

                const isSubscribed =
                    ppvSubscribedList.length > 0 && ppvSubscribedList.some((sub) => sub.eventId === event.eventId);

                if (isSubscribed && window.location.search.includes('success=true')) {
                    track(EVENT.PPV.PPV_PURCHASED_COMPLETED, {
                        'Event Title': event?.name,
                        'Event ID': event?.eventId,
                        'Purchase Method': 'CC',
                        Platform: 'Web',
                    });
                    onPurchaseSuccess(null);
                    removeSearchParams(['success']);
                    toast(__('ppv_purchase_success'), 'default', {
                        notDismissible: true,
                        icon: <FontIcon name="check-circle" size="large" md="large" color="green" />,
                    });
                }
                const { eventBroadcastTs, programGuid, eventExpiryDate } = event || {};
                const cmsPPVEvent = cmsPPVEvents?.find(
                    (cmsEvent) => cmsEvent?.subscriptionGuid === event?.subscriptionGuid
                );
                const { eventEndTimeTs } = cmsPPVEvent || {};
                const timeObj = { startTime: eventBroadcastTs, endTime: eventEndTimeTs };
                const isLive = isNow(timeObj);
                const isCompleted = isEarlier(timeObj);
                const rewindDays = isCompleted ? Math.ceil(moment(eventExpiryDate).diff(moment(), 'days', true)) : null;

                // Only update if the event has changed to prevent infinite loop
                if (selectedPPVEvent?.eventId !== event.eventId) {
                    setSelectedPPVEvent({
                        ...event,
                        status: isSubscribed ? 'Subscribed' : null,
                        statusColor: isSubscribed ? 'green' : null,
                        eventData,
                        isLive,
                        isCompleted,
                        rewindDays,
                    });
                }
            }
        }
    }, [ppvEvents, selectedPPVId, ppvSubscribedList, promotions, userChannels?.channels?.length]);
};
